<template>
    <div>
        <div class="infinite-scroll-container" ref="scrollContainer">
            <div class="events-grid">
                <!-- Visible Events -->
                <div class="card" v-for="(item, index) in visibleEvents" :key="index" @click="goToDetails(item.id)">
                    <div class="card-container">
                        <img :src="item.imageUrl" :alt="item.title" class="card-image" v-if="item.imageUrl" />
                        <div v-else class="card-placeholder">Image not available</div>
                        <div class="city-text">{{ item.city }}</div>
                    </div>
                    <div class="card-content">
                        <h3 class="card-title">{{ item.title }}</h3>
                        <div class="card-container">
                            <p class="card-rating">
                                <i class="fas fa-map-marker-alt icon"></i>
                                <span class="street-text">{{ item.street }}</span>
                            </p>
                            <p class="card-rating">
                                <i class="fas fa-clock icon"></i>
                                <span class="date-text">{{ formatDate(item.startDate) }} {{ formatTime(item.startTime) }}</span>
                            </p>
                        </div>
                        <div class="card-platforms">
                            <span v-for="platform in item.platforms" :key="platform" class="platform">
                                {{ platform }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isLoading && showShimmer" class="events-grid">
                <div class="shimmer-card card" v-for="n in 2" :key="n"></div>
            </div>

            <div v-if="hasError" class="error-message">Failed to load events. Please try again later.</div>
            <div class="scroll-sentinel"></div>
        </div>

        <b-button variant="light" v-if="showScrollToTop" @click="scrollToTop" class="scroll-to-top">
            ↑ Skrolla till början ↑
        </b-button>
    </div>
</template>

<script>
import { toRaw } from "vue";

export default {
    props: {
        events: {
            type: Array,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            hasError: false,
            initLoad: true,
            showScrollToTop: false,
            scrollObserver: null,
            visibleEvents: [],
            showShimmer: true,
            noMoreToFetch: false
        };
    },
    mounted() {
        this.setupScrollObserver();
    },
    watch: {
        events: {
            handler(newEvents) {
                if (toRaw(newEvents).length === 0) {
                    this.showShimmer = false;
                    return;
                }

                this.noMoreToFetch = toRaw(newEvents).length % 10 !== 0;
                this.visibleEvents = [...toRaw(newEvents)];
                this.showShimmer = !this.noMoreToFetch;
                this.initLoad = false;
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        goToDetails(id) {
            window.location.href = `/eventdetails/${id}`;
        },
        requestMoreEvents() {
            this.$emit("fetch-more-events");
        },
        setupScrollObserver() {
            const options = {
                root: this.$refs.scrollContainer,
                rootMargin: '0px',
                threshold: 1.0,
            };

            this.scrollObserver = new IntersectionObserver(this.handleScroll, options);
            const sentinel = this.$refs.scrollContainer.querySelector('.scroll-sentinel');
            if (sentinel) {
                this.scrollObserver.observe(sentinel);
            }
        },
        handleScroll() {
            
            if (this.isLoading || this.noMoreToFetch) return;
            const container = this.$refs.scrollContainer;
            if (!container) return;

            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;

            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
            if (scrollPercentage >= 0.4 && !this.isLoading) {
                this.requestMoreEvents();
            }
        },
        scrollToTop() {
            const container = this.$refs.scrollContainer;
            if (container) {
                container.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            }
        },
        formatDate(dateString) {
            return dateString.split("T")[0];
        },
        formatTime(timeString) {
            return timeString.split(":").slice(0, 2).join(":");
        },
    },
    beforeUnmount() {
        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
        }
    },
};
</script>

<style scoped>
.infinite-scroll-container {
    height: 400px;
    overflow-y: auto;
}

.events-grid {
    padding: 20px;
    padding-top: 0px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
}

.card {
    box-sizing: border-box;
    background-color: rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    height: 220px;
    overflow: hidden;
    border-radius: 10px;
    border: none;
    cursor: pointer;
}

.card-container {
    position: relative;
    width: 100%;
    height: 130px;
}

.card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.card-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 14px;
}

.fa-clock{
    font-size: 9px;
}

.date-text{
    position: relative;
    left: 4px;
}

.street-text{
    position: relative;
    left: 5px;
}

.city-text {
    position: absolute;
    bottom: 2px;
    right: 3px;
    color: white;
    font-size: 12px;
    background: rgba(0, 0, 0, 0.7);
    padding: 5px 10px;
    border-radius: 5px;
}

.card-content {
    padding: 10px;
    flex-grow: 1;
    overflow: hidden;
}

.card-title {
    font-size: 12px;
    color: white;
}

.card-rating {
    font-size: 10px;
    font-weight: 600;
    color: white;
    margin-bottom: 5px;
}

.card-platforms {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    color: white;
}

.platform {
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 8px;
}

@media (min-width: 1401px) {
    .events-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 1400px) {
    .events-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 999px) {
    .events-grid {
        grid-template-columns: repeat(1, 1fr);
    }
}
</style>
